import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMembershipsRelayQuery } from '../../hooks/membership/query/V2/useMembershipsRelay';
import { useUserMembershipsRelayQuery } from '../../hooks/membership/query/V2/useUserMembershipsRelay';
import { mapMembershipsToLegacy } from '../../hooks/membership/query/V2/membershipMappers';
import useSearchDebounce from '../../hooks/search/useSearchDebounce';
import { ISystem } from '../../ts/interface/system.interface';
import { connect } from 'react-redux';
import MembershipList from './assets/membership-list';
import PageContainer from '../../components/Redesing/page-container';
import PageContent from '../../components/Redesing/page-content';
import { changeTab } from '../../utils';
import { useParams } from 'react-router-dom';
import PendingMemberships from './pending-memberships';

import useUrlState from '../../hooks/use-url-state';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants';
import { ISortConfig } from '../../ts/interface/components/redesing/table.interface';
import { useKeysetPagination } from '../../hooks/use-keyset-pagination';

const MembershipPage = ({
  system,
  systemLoaded,
}: {
  system: ISystem;
  systemLoaded: boolean;
}) => {
  const { currentTab = 'memberships' } = useParams<{ currentTab: string }>();
  const [currentTabV, setCurrentTabV] = useState(currentTab);
  const handleTabsChange = useCallback(
    (event: React.SyntheticEvent, value: React.SetStateAction<string>) => {
      changeTab(value, currentTab, 'memberships');
    },
    [currentTab]
  );

  const [filters, setFilters, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    status: 'active',
    sortConfig: {
      key: 'created_on',
      direction: 'asc',
    } as ISortConfig,
  });

  // Shared server-side keyset pagination (page# kept local, not in the URL).
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();

  useEffect(() => {
    if (currentTab !== currentTabV) {
      setCurrentTabV(currentTab);
    }
  }, [currentTab, currentTabV]);

  const debouncedSearchTerm = useSearchDebounce(filters.search);

  // Convert sort config to GraphQL format
  const sortConfig = useMemo(() => {
    const keyMap: Record<string, string> = {
      created_on: 'CREATED_ON',
      name: 'NAME',
      active_on: 'ACTIVE_ON',
      priority: 'PRIORITY',
    };
    const field = keyMap[filters.sortConfig?.key || 'created_on'] || 'CREATED_ON';
    const order =
      filters.sortConfig?.direction?.toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
    return [{ field: field as 'CREATED_ON' | 'NAME', order: order as 'ASC' | 'DESC' }];
  }, [filters.sortConfig]);

  const {
    data: membershipsData,
    count: totalMembershipCount,
    isRefetching,
    isLoading,
    hasMore: graphqlHasMore,
    endCursor,
  } = useMembershipsRelayQuery(
    {
      systemId: system.gid,
      name: debouncedSearchTerm || undefined,
      // Empty status (filter removed) lists all memberships.
      status: (filters.status?.toLowerCase() || 'all') as
        | 'active'
        | 'archived'
        | 'future'
        | 'all',
    },
    {
      limit: filters.sizePerPage,
      sort: sortConfig,
      enabled: systemLoaded && isComplete,
      after,
    }
  );

  // Lightweight count-only query for the pending tab badge. The pending list
  // itself is fetched (and server-paginated) inside <PendingMemberships>.
  const { count: pendingCount } = useUserMembershipsRelayQuery(
    {
      systemId: system.gid,
      pending: true,
    },
    {
      enabled: systemLoaded,
      limit: 1,
    }
  );

  // Render ONLY the current page (no client-side accumulation).
  const membershipsItems = useMemo(
    () => (membershipsData ? mapMembershipsToLegacy(membershipsData) : []),
    [membershipsData]
  );

  // Record the next page's cursor and clear the paginating loader when a fetch
  // settles.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipsData]);

  // Total pending awaiting approval (authoritative server count).
  const pendingCountTotal = pendingCount ?? 0;

  const warning = useCallback(() => {
    const noActiveMemberships = membershipsItems?.length === 0;
    const isPrivateSystem = system.privateSystem;
    const noStatusFilter = !filters.status;
    const noSearchTerm = !debouncedSearchTerm;
    const hasPendingMemberships = pendingCountTotal > 0;

    if (
      noActiveMemberships &&
      isPrivateSystem &&
      !isLoading &&
      noStatusFilter &&
      noSearchTerm
    ) {
      return 'There are no active memberships and the system is private. Please add a membership for users to join the system.';
    }

    if (hasPendingMemberships) {
      const membershipWord = pendingCountTotal === 1 ? 'membership' : 'memberships';
      return `You have ${pendingCountTotal} pending user ${membershipWord} waiting for approval`;
    }
    return undefined;
  }, [
    membershipsItems,
    system,
    isLoading,
    pendingCountTotal,
    filters,
    debouncedSearchTerm,
  ]);

  return (
    <PageContainer
      isLoading={!systemLoaded}
      warningMessage={warning()}
      spaceName={'Memberships'}
    >
      <PageContent
        detailedView={false}
        tabsList={[
          { label: 'Memberships', value: 'memberships' },
          {
            label: 'Pending Memberships',
            value: 'pending',
            warnings: pendingCountTotal,
          },
        ]}
        tabsChildren={
          <>
            {currentTabV === 'memberships' && (
              <MembershipList
                isRestoring={isRestoring}
                memberships={membershipsItems}
                isLoading={isLoading || isPaginating}
                isLoadingCache={isRefetching}
                hasMore={graphqlHasMore}
                totalItems={totalMembershipCount}
                pageNumber={pageNumber}
                filters={filters}
                setFilters={setFilters}
                isFilterComplete={isComplete}
                onSizePerPageChange={(sizePerPage) => {
                  resetPagination();
                  setFilters((prevState) => ({
                    ...prevState,
                    sizePerPage,
                  }));
                }}
                onPageChange={(newPage) => onPageChange(newPage)}
                onSearchChange={() => resetPagination()}
                onSortChange={() => resetPagination()}
                onFilterApply={() => resetPagination()}
              />
            )}
            {currentTabV === 'pending' && (
              <PendingMemberships scope={{ systemId: system.gid }} />
            )}
          </>
        }
        currentTabV={currentTabV}
        handleTabsChange={handleTabsChange}
      />
    </PageContainer>
  );
};

export default connect(
  (state: { system: { current: ISystem; isLoaded: boolean } }) => ({
    system: state.system.current,
    systemLoaded: state.system.isLoaded,
  }),
  () => ({})
)(MembershipPage);
